import {
  takeLatest,
  takeLeading,
  put,
  call,
  select,
  takeEvery
} from 'redux-saga/effects';
import { push } from 'connected-react-router';
import { actions as userMessaging } from 'store/userMessaging';
import { getDefaultLandingPathForTenant } from 'helpers';

import {
  loginIfNeeded,
  logoutIfNeeded,
  clearStore,
  resetPassword,
  checkAdsaml,
  callRAuth,
  getSsoLogin,
  forgotPassword,
  forgotUsername,
  sendForgotPassword,
  refreshToken,
  handleStoreClearing,
  sessionStart,
  sessionEnding,
  getLicensesData,
  getLogoDetails,
  getAuthenticationData,
  loginWithAuthentication,
  sendCodeThroughSms,
  sendCodeThroughEmail,
  getSupportUsers,
  loginWithSupportAccess,
  loginAsSupportUser,
  refreshSessionToken,
  getTenantList,
  getSelectedTenantContextDetails,
  getTenantDetails
} from './sagas';
import {
  LOG_IN,
  LOG_OUT,
  LOGGING_IN_SUCCEEDED,
  LOGGING_OUT_SUCCEEDED,
  SET_SELECTED_TENANT,
  SET_SELECTED_BUSINESSCONTEXT,
  CHECK_ADSAML,
  CHECKING_ADSAML_SUCCEEDED,
  SSO_LOG_IN,
  RESET_PASSWORD,
  FORGOT_PASSWORD,
  FORGOT_USERNAME,
  SEND_FORGOT_PASSWORD,
  REFRESH_TOKEN,
  SESSION_START,
  SESSION_ENDING,
  LOGGING_IN_FAILED,
  GET_LICENSES,
  GET_LOGO_DETAILS,
  GETTING_LICENSES_FAILED,
  GET_AUTHENTICATION,
  PROCEED_AUTHENTICATION,
  SEND_CODE_THROUGH_SMS,
  SEND_CODE_THROUGH_EMAIL,
  GET_SUPPORT_USER_DETAILS,
  SUPPORT_USER_SELECTED,
  LOGIN_SUCCESS,
  LOGIN_AS_SUPPORT_USER,
  REFRESH_SESSION_TOKEN,
  GETTING_LOGIN_DETAILS_SUCCEEDED,
  SET_CONTEXT_DETAILS,
  SUPPORT_USER_LOGIN_SUCCEEDED,
  GET_TENANT_LIST
} from './actions';

/* APP FLOW - User */
function* HandleFailuresSaga() {
  yield takeLatest([GETTING_LICENSES_FAILED], function*(action) {
    yield put(
      userMessaging.setMessageForFailedAction({
        type: action.type,
        error: action.payload || action.error
      })
    );
  });
}

function* UserLoginSaga() {
  yield takeLeading(LOG_IN, function*(action) {
    yield put(userMessaging.clearMessage());
    yield call(loginIfNeeded, action);
  });
}

function* SsoLoginSaga() {
  yield takeLeading(SSO_LOG_IN, function*(action) {
    yield call(getSsoLogin, action);
  });
}

function* checkAdsamlSaga() {
  yield takeLeading(CHECK_ADSAML, function*(action) {
    yield call(checkAdsaml, action);
  });
}
function* callRAuthSaga() {
  yield takeLeading(CHECKING_ADSAML_SUCCEEDED, function*(action) {
    if (action.isAdsamlEnabled) {
      yield call(callRAuth, action);
    }
  });
}

function* UserResetPasswordSaga() {
  yield takeLeading(RESET_PASSWORD, function*(action) {
    yield call(resetPassword, action);
  });
}

function* UserForgotPasswordSaga() {
  yield takeLeading(FORGOT_PASSWORD, function*(action) {
    yield call(forgotPassword, action);
  });
}

function* UserForgotUsernameSaga() {
  yield takeLeading(FORGOT_USERNAME, function*(action) {
    yield call(forgotUsername, action);
  });
}

function* UserSendForgotPassword() {
  yield takeLeading(SEND_FORGOT_PASSWORD, function*(action) {
    yield call(sendForgotPassword, action);
  });
}

function* UserLogoutSaga() {
  yield takeLatest(LOG_OUT, function*(action) {
    yield call(logoutIfNeeded, action.options);
    yield call(clearStore);
  });
}

function* UserRefreshTokenSaga() {
  yield takeLeading(REFRESH_TOKEN, function*(action) {
    yield call(refreshToken, action);
  });
}

function* UserRefreshSessionTokenSaga() {
  yield takeLeading(REFRESH_SESSION_TOKEN, function*(action) {
    yield call(refreshSessionToken, action);
  });
}

function* UserRefreshSessionSaga() {
  yield takeEvery(SESSION_START, function*(action) {
    yield call(sessionStart, action);
  });
}

function* UserSessionEndingSaga() {
  yield takeEvery(SESSION_ENDING, function*(action) {
    yield call(sessionEnding, action);
  });
}

function* UserLoginSuccessSaga() {
  yield takeLatest(LOGIN_SUCCESS, function*() {
    const selectedTenantId = yield select(store => store.user.selectedTenant?.tenantId);
    const tenantId = selectedTenantId ?? window.localStorage.getItem('GCM-tenantId');
    const defaultLandingPath = getDefaultLandingPathForTenant(tenantId);
    const currentPath = yield select(store => store.router.location.pathname);
    if (
      currentPath === '/sign-in' ||
      currentPath === '/reset-password' ||
      currentPath === '/authentication' ||
      currentPath.includes('/ssoLogin')
    ) {
      yield put(push(defaultLandingPath));
      const lastTenantId = window.localStorage.getItem('GCM-tenantId');      
      yield call(getTenantDetails, lastTenantId);        
    }
  });
}
function* UserLogoutSuccessSaga() {
  yield takeLatest(LOGGING_OUT_SUCCEEDED, function*(action) {
    if (window.localStorage.getItem('isTurnstileRefreshNeeded') === 'true') {
      window.turnstile.reset();
    }
    const currentPath = yield select(store => store.router.location.pathname);
    let preventRedirect = action?.options?.preventRedirect;
    window.localStorage.removeItem('GCM-xToken');
    window.localStorage.removeItem('GCM-tenantId');
    window.localStorage.removeItem('GCM-businessContextId');
    if (!preventRedirect && !currentPath.includes('/ssoLogin') && !currentPath.includes('/sign-in')) {
      // Use full page reload to ensure clean state after logout
      window.location.href = window.location.origin + (window._env_?.REACT_APP_BASENAME || '/gcm/') + 'sign-in';
    }
  });
}

function* UserLoginFailedSaga() {
  yield takeLatest(LOGGING_IN_FAILED, function*() {
    if (window.localStorage.getItem('isTurnstileRefreshNeeded') === 'true') {
      window.turnstile.reset();
    }
    const currentPath = yield select(store => store.router.location.pathname);
    if (window.self !== window.top) { //Condition for finding iFrame
      yield put(push(`/not-authorized`));
    } else if (!currentPath.includes('/ssoLogin')) {
      yield put(push('/sign-in'));
    }
  });
}

function* UserSetSelectedTenantSaga() {
  yield takeLatest(SET_SELECTED_TENANT, function*(action) {
    yield window.localStorage.setItem('GCM-tenantId', action.tenantId);
    yield call(getSelectedTenantContextDetails, action.tenantId);
    if (action.clearStore) {
      yield call(handleStoreClearing);
    }
  });
}
function* UserSetSelectedBusinessContextSaga() {
  yield takeLatest(SET_SELECTED_BUSINESSCONTEXT, function*(action) {
    yield window.localStorage.setItem(
      'GCM-businessContextId',
      action.businessContextId
    );
    if (action.clearStore) {
      yield call(handleStoreClearing);
    }
  });
}

function* UserGettingLoginDetailsSucceededSaga() {
  yield takeLatest(GETTING_LOGIN_DETAILS_SUCCEEDED, function*(action) {
    const selectedBusinessContext = store => store.user.selectedBusinessContext;
    const contextId =
      (yield select(selectedBusinessContext))?.businessContextId || '';
    yield window.localStorage.setItem('GCM-businessContextId', contextId);
  });
}

function* UserSetContextDetailsSaga() {
  yield takeLatest(SET_CONTEXT_DETAILS, function*(action) {
    if((window.self === window.top)) {
      const selectedBusinessContext = store => store.user.selectedBusinessContext;
      const contextId =
        (yield select(selectedBusinessContext))?.businessContextId || '';
      yield window.localStorage.setItem('GCM-businessContextId', contextId);
    }
  });
}
function* UserSupportUserLoginSucceededSaga() {
  yield takeLatest(SUPPORT_USER_LOGIN_SUCCEEDED, function*(action) {
    const selectedBusinessContext = store => store.user.selectedBusinessContext;
    const contextId =
      (yield select(selectedBusinessContext))?.businessContextId || '';
    yield window.localStorage.setItem('GCM-businessContextId', contextId);
  });
}

function* UserGetLicensesDataSaga() {
  yield takeEvery(GET_LICENSES, function*(action) {
    yield call(getLicensesData, action);
  });
}

function* UserGetLogoDetailsSaga() {
  yield takeEvery(GET_LOGO_DETAILS, function*(action) {
    yield call(getLogoDetails, action);
  });
}

function* UserGetAuthenticationSaga() {
  yield takeLatest(GET_AUTHENTICATION, function*(action) {
    yield call(getAuthenticationData, action);
  });
}

function* UserProceedAuthenticationSaga() {
  yield takeLeading(PROCEED_AUTHENTICATION, function*(action) {
    yield call(loginWithAuthentication, action);
  });
}

function* UserSendCodeThroughSmsSaga() {
  yield takeEvery(SEND_CODE_THROUGH_SMS, function*(action) {
    yield call(sendCodeThroughSms, action);
  });
}

function* UserSendCodeThroughEmailSaga() {
  yield takeEvery(SEND_CODE_THROUGH_EMAIL, function*(action) {
    yield call(sendCodeThroughEmail, action);
  });
}

function* UserGetSupportUserDetailsSaga() {
  yield takeEvery(GET_SUPPORT_USER_DETAILS, function*(action) {
    yield call(getSupportUsers, action);
  });
}

function* UserLoginAsSupportUserSaga() {
  yield takeLatest(LOGIN_AS_SUPPORT_USER, function*(action) {
    yield call(loginAsSupportUser, action);
  });
}

function* UserLoginWithSupportUserSaga() {
  yield takeLatest(SUPPORT_USER_SELECTED, function*(action) {
    yield call(loginWithSupportAccess, action);
  });
}

function* UserTenantListSaga() {
  yield takeLatest(GET_TENANT_LIST, function*(action) {
    yield call(getTenantList, action);
  })
}

export default [
  HandleFailuresSaga,
  UserLoginSaga,
  UserLogoutSaga,
  UserLoginSuccessSaga,
  UserLogoutSuccessSaga,
  UserSetSelectedTenantSaga,
  UserSetSelectedBusinessContextSaga,
  checkAdsamlSaga,
  callRAuthSaga,
  SsoLoginSaga,
  UserResetPasswordSaga,
  UserForgotPasswordSaga,
  UserForgotUsernameSaga,
  UserSendForgotPassword,
  UserRefreshTokenSaga,
  UserRefreshSessionSaga,
  UserSessionEndingSaga,
  UserLoginFailedSaga,
  UserGetLicensesDataSaga,
  UserGetLogoDetailsSaga,
  UserGetAuthenticationSaga,
  UserProceedAuthenticationSaga,
  UserSendCodeThroughSmsSaga,
  UserSendCodeThroughEmailSaga,
  UserGetSupportUserDetailsSaga,
  UserLoginAsSupportUserSaga,
  UserLoginWithSupportUserSaga,
  UserRefreshSessionTokenSaga,
  UserGettingLoginDetailsSucceededSaga,
  UserSetContextDetailsSaga,
  UserSupportUserLoginSucceededSaga,
  UserTenantListSaga
];
